import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, eachMonthOfInterval, eachWeekOfInterval, parseISO, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import jsPDF from 'jspdf';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../context/AuthContext';
import ActionButton from '../../components/ui/ActionButton';
import {
  getAssessmentV2Label,
  getAssessmentV2ResultLabel,
} from '../../features/assessments-v2/registry/assessmentRegistry';
import { getMedicalRecordAuthorFromAttachments } from '../../utils/medicalRecordAuthor';
import {
  getMedicalRecordAttachmentsObject,
  isMedicalRecordInvalidated,
} from '../../utils/medicalRecordStatus';

type PeriodMode = 'range' | 'all';
type Granularity = 'month' | 'week' | 'day';
type MetricKey = 'appointments' | 'evolutions' | 'assessments' | 'professionals';
type ReportEntryType = 'appointment' | 'evolution' | 'assessment';

interface ReportEntry {
  id: string;
  sourceType: ReportEntryType;
  title: string;
  startDate: string;
  endDate?: string | null;
  professionalKey: string;
  professionalName: string;
  patientId: string;
  location?: string | null;
  durationMinutes: number | null;
  statusLabel: string;
}

interface ProfessionalSummaryRow {
  professionalKey: string;
  professionalName: string;
  appointments: number;
  evolutions: number;
  assessments: number;
  total: number;
}

interface ReportTotals {
  appointments: number;
  evolutions: number;
  assessments: number;
  professionals: number;
}

const MONTH_FORMAT = 'yyyy-MM';
const DEFAULT_START_MONTH = format(subMonths(new Date(), 11), MONTH_FORMAT);
const DEFAULT_END_MONTH = format(new Date(), MONTH_FORMAT);

const METRIC_DEFS: Array<{ key: MetricKey; label: string; color: string }> = [
  { key: 'appointments', label: 'Agendamentos', color: '#2563eb' },
  { key: 'evolutions', label: 'Evoluções', color: '#10b981' },
  { key: 'assessments', label: 'Avaliações', color: '#f59e0b' },
  { key: 'professionals', label: 'Profissionais únicos', color: '#8b5cf6' },
];

const parseMonthKey = (value: string): Date | null => {
  const [year, month] = value.split('-').map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return null;
  return new Date(year, month - 1, 1);
};

const getBucketKey = (date: Date, granularity: Granularity): string => {
  if (granularity === 'day') return format(date, 'yyyy-MM-dd');
  if (granularity === 'week') return format(date, "yyyy-'W'II", { locale: ptBR });
  return format(date, MONTH_FORMAT);
};

const getBucketLabel = (date: Date, granularity: Granularity): string => {
  if (granularity === 'day') return format(date, 'dd/MM', { locale: ptBR });
  if (granularity === 'week') return `Sem ${format(date, 'II/yy', { locale: ptBR })}`;
  return format(date, 'MMM/yy', { locale: ptBR });
};

const buildBucketDates = (start: Date, end: Date, granularity: Granularity): Date[] => {
  if (granularity === 'day') return eachDayOfInterval({ start, end });
  if (granularity === 'week') return eachWeekOfInterval({ start, end }, { weekStartsOn: 1 });
  return eachMonthOfInterval({ start, end });
};

const computeMovingAverage = (values: number[], window = 3): number[] =>
  values.map((_, index) => {
    const startIndex = Math.max(0, index - window + 1);
    const slice = values.slice(startIndex, index + 1);
    if (!slice.length) return 0;
    const total = slice.reduce((sum, value) => sum + value, 0);
    return total / slice.length;
  });

const getProfessionalLabel = (input: { professionalName?: string | null; userId?: string | null }): string =>
  input.professionalName?.trim() || input.userId || 'Não atribuído';

const getProfessionalKey = (input: { professionalName?: string | null; userId?: string | null }): string => {
  const normalizedName = input.professionalName?.trim();
  if (normalizedName) return normalizedName.toLocaleLowerCase('pt-BR');
  return input.userId || 'nao-atribuido';
};

const getAppointmentStatusLabel = (status: string): string => {
  switch (status) {
    case 'completed':
      return 'Concluído';
    case 'cancelled':
      return 'Cancelado';
    case 'no_show':
      return 'Falta';
    case 'scheduled':
    default:
      return 'Agendado';
  }
};

const getEvolutionStatusLabel = (linkedAppointmentId?: string | null): string =>
  linkedAppointmentId ? 'Com agendamento vinculado' : 'Sem agendamento vinculado';

const getSourceLabel = (sourceType: ReportEntryType): string => {
  switch (sourceType) {
    case 'appointment':
      return 'Agendamento';
    case 'evolution':
      return 'Evolução';
    case 'assessment':
    default:
      return 'Avaliação';
  }
};

const buildAssessmentTitle = (labels: string[], fallback = 'Avaliação'): string => {
  if (!labels.length) return fallback;
  if (labels.length === 1) return labels[0];
  if (labels.length <= 3) return labels.join(', ');
  return `Avaliação (${labels.length} testes)`;
};

const getCompletedAssessmentStatusLabel = (testCount: number): string => {
  if (testCount <= 1) return 'Concluída';
  return `Concluída (${testCount} testes)`;
};

const getAssessmentSessionStatusLabel = (statuses: string[], testCount: number): string => {
  const hasDraft = statuses.includes('draft');
  const hasCompleted = statuses.includes('completed');

  if (hasDraft && hasCompleted) {
    return `Parcial (${testCount} testes)`;
  }

  if (hasDraft) {
    return `Rascunho (${testCount} testes)`;
  }

  return getCompletedAssessmentStatusLabel(testCount);
};

const resolveMonthRange = (startMonth: string, endMonth: string): { start: Date; end: Date } => {
  const startCandidate = parseMonthKey(startMonth) ?? new Date();
  const endCandidate = parseMonthKey(endMonth) ?? new Date();

  if (startCandidate.getTime() <= endCandidate.getTime()) {
    return {
      start: startOfMonth(startCandidate),
      end: endOfMonth(endCandidate),
    };
  }

  return {
    start: startOfMonth(endCandidate),
    end: endOfMonth(startCandidate),
  };
};

const AgendaReports = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [appointmentEntries, setAppointmentEntries] = useState<ReportEntry[]>([]);
  const [evolutionEntries, setEvolutionEntries] = useState<ReportEntry[]>([]);
  const [assessmentEntries, setAssessmentEntries] = useState<ReportEntry[]>([]);
  const [periodMode, setPeriodMode] = useState<PeriodMode>('range');
  const [startMonth, setStartMonth] = useState(DEFAULT_START_MONTH);
  const [endMonth, setEndMonth] = useState(DEFAULT_END_MONTH);
  const [granularity, setGranularity] = useState<Granularity>('month');
  const [professionalFilter, setProfessionalFilter] = useState('all');
  const [primaryMetric, setPrimaryMetric] = useState<MetricKey>('appointments');
  const [lineMetrics, setLineMetrics] = useState<MetricKey[]>(['evolutions', 'assessments']);
  const [showTrend, setShowTrend] = useState(true);
  const reportRef = useRef<HTMLDivElement | null>(null);
  const reportSourceRequestRef = useRef<{ requestId: number; clientId?: string }>({
    requestId: 0,
    clientId: undefined,
  });

  const { selectedClient } = useAuth();
  const clientId = selectedClient?.clientId;

  const loadReportSources = useCallback(async (requestedClientId: string, requestId: number) => {
    const isCurrentRequest = () =>
      reportSourceRequestRef.current.requestId === requestId
      && reportSourceRequestRef.current.clientId === requestedClientId;

    if (!isCurrentRequest()) return;

    try {
      const [appointmentsResult, evolutionsResult, legacyAssessmentsResult, v2ResultsResult] = await Promise.all([
        supabase
          .from('appointments')
          .select(
            `
            appointmentId,
            appointmentTitle,
            appointmentStartDate,
            appointmentEndDate,
            userId,
            patientId,
            appointmentLocation,
            appointmentStatus,
            clientId,
            users!appointments_userId_fkey (userAssignedCalendarName, userDisplayName)
            `,
          )
          .eq('clientId', requestedClientId)
          .order('appointmentStartDate', { ascending: false }),
        supabase
          .from('medical_records')
          .select(
            `
            recordId,
            recordTitle,
            recordDate,
            doctorId,
            patientId,
            clientId,
            recordType,
            recordAttachments,
            users (userAssignedCalendarName, userDisplayName)
            `,
          )
          .eq('clientId', requestedClientId)
          .eq('recordType', 'progress')
          .order('recordDate', { ascending: false }),
        supabase
          .from('medical_records')
          .select(
            `
            recordId,
            recordTitle,
            recordDate,
            doctorId,
            patientId,
            clientId,
            recordType,
            recordAttachments,
            users (userAssignedCalendarName, userDisplayName)
            `,
          )
          .eq('clientId', requestedClientId)
          .eq('recordType', 'evaluation')
          .order('recordDate', { ascending: false }),
        supabase
          .from('assessment_results_v2')
          .select(
            `
            resultId,
            sessionId,
            testKey,
            assessmentDate,
            patientId,
            clientId,
            professionalUserId,
            professionalNameSnapshot,
            summaryJson,
            status
            `,
          )
          .eq('clientId', requestedClientId)
          .neq('status', 'cancelled')
          .order('assessmentDate', { ascending: false }),
      ]);

      if (appointmentsResult.error) throw appointmentsResult.error;
      if (evolutionsResult.error) throw evolutionsResult.error;
      if (legacyAssessmentsResult.error) throw legacyAssessmentsResult.error;
      if (v2ResultsResult.error) throw v2ResultsResult.error;
      if (!isCurrentRequest()) return;

      const v2ProfessionalIds = Array.from(
        new Set(
          ((v2ResultsResult.data || []) as any[])
            .map((row) => row.professionalUserId)
            .filter((value): value is string => typeof value === 'string' && value.trim().length > 0),
        ),
      );

      const professionalDirectory = new Map<string, { userDisplayName?: string | null; userAssignedCalendarName?: string | null }>();

      if (v2ProfessionalIds.length > 0) {
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('userId, userDisplayName, userAssignedCalendarName')
          .in('userId', v2ProfessionalIds);

        if (usersError) throw usersError;

        (usersData || []).forEach((row: any) => {
          professionalDirectory.set(row.userId, {
            userDisplayName: row.userDisplayName,
            userAssignedCalendarName: row.userAssignedCalendarName,
          });
        });
      }

      const nextAppointmentEntries: ReportEntry[] = ((appointmentsResult.data || []) as any[]).map((row) => {
        const professionalName =
          row.users?.userDisplayName || row.users?.userAssignedCalendarName || row.userId || undefined;

        return {
          id: row.appointmentId,
          sourceType: 'appointment',
          title: String(row.appointmentTitle || 'Agendamento'),
          startDate: row.appointmentStartDate,
          endDate: row.appointmentEndDate,
          professionalKey: getProfessionalKey({ professionalName, userId: row.userId }),
          professionalName: getProfessionalLabel({ professionalName, userId: row.userId }),
          patientId: row.patientId,
          location: row.appointmentLocation || null,
          durationMinutes:
            row.appointmentStartDate && row.appointmentEndDate
              ? Math.max(
                  0,
                  Math.round(
                    (moment(row.appointmentEndDate).valueOf() - moment(row.appointmentStartDate).valueOf()) /
                      (1000 * 60),
                  ),
                )
              : null,
          statusLabel: getAppointmentStatusLabel(row.appointmentStatus || 'scheduled'),
        };
      });

      const nextEvolutionEntries: ReportEntry[] = ((evolutionsResult.data || []) as any[])
        .filter((row) => !isMedicalRecordInvalidated(row.recordType, row.recordAttachments))
        .map((row) => {
          const attachments = getMedicalRecordAttachmentsObject(row.recordAttachments);
          const snapshotAuthor = getMedicalRecordAuthorFromAttachments(row.recordAttachments);
          const authorUser = Array.isArray(row.users) ? row.users[0] : row.users;
          const professionalName =
            snapshotAuthor?.name ||
            authorUser?.userDisplayName ||
            authorUser?.userAssignedCalendarName ||
            row.doctorId ||
            undefined;
          const linkedAppointmentId =
            typeof attachments.agendamentoId === 'string' && attachments.agendamentoId.trim().length > 0
              ? attachments.agendamentoId
              : null;

          return {
            id: row.recordId,
            sourceType: 'evolution',
            title: row.recordTitle || 'Evolução clínica',
            startDate: row.recordDate,
            endDate: null,
            professionalKey: getProfessionalKey({ professionalName, userId: row.doctorId }),
            professionalName: getProfessionalLabel({ professionalName, userId: row.doctorId }),
            patientId: row.patientId,
            location: null,
            durationMinutes: null,
            statusLabel: getEvolutionStatusLabel(linkedAppointmentId),
          };
        });

      const legacyAssessmentEntries: ReportEntry[] = ((legacyAssessmentsResult.data || []) as any[])
        .filter((row) => !isMedicalRecordInvalidated(row.recordType, row.recordAttachments))
        .map((row) => {
          const attachments = getMedicalRecordAttachmentsObject(row.recordAttachments);
          const snapshotAuthor = getMedicalRecordAuthorFromAttachments(row.recordAttachments);
          const authorUser = Array.isArray(row.users) ? row.users[0] : row.users;
          const professionalName =
            snapshotAuthor?.name ||
            authorUser?.userDisplayName ||
            authorUser?.userAssignedCalendarName ||
            row.doctorId ||
            undefined;
          const rawTests = Array.isArray(attachments.tests)
            ? attachments.tests.map((value: unknown) => String(value)).filter((value: string) => value.trim().length > 0)
            : [];
          const assessmentLabels = Array.from(new Set(rawTests.map((value) => getAssessmentV2Label(value))));
          const title =
            assessmentLabels.length > 0
              ? buildAssessmentTitle(assessmentLabels, row.recordTitle || 'Avaliação')
              : row.recordTitle || 'Avaliação';

          return {
            id: row.recordId,
            sourceType: 'assessment',
            title,
            startDate: row.recordDate,
            endDate: null,
            professionalKey: getProfessionalKey({ professionalName, userId: row.doctorId }),
            professionalName: getProfessionalLabel({ professionalName, userId: row.doctorId }),
            patientId: row.patientId,
            location: null,
            durationMinutes: null,
            statusLabel: getCompletedAssessmentStatusLabel(assessmentLabels.length || 1),
          };
        });

      const sessionMap = new Map<
        string,
        {
          sessionId: string;
          assessmentDate: string;
          patientId: string;
          professionalUserId?: string | null;
          professionalName?: string;
          statuses: string[];
          testLabels: string[];
        }
      >();

      ((v2ResultsResult.data || []) as any[]).forEach((row) => {
        if (!sessionMap.has(row.sessionId)) {
          const directoryUser = row.professionalUserId ? professionalDirectory.get(row.professionalUserId) : undefined;
          const professionalName =
            row.professionalNameSnapshot ||
            directoryUser?.userDisplayName ||
            directoryUser?.userAssignedCalendarName ||
            row.professionalUserId ||
            undefined;

          sessionMap.set(row.sessionId, {
            sessionId: row.sessionId,
            assessmentDate: row.assessmentDate,
            patientId: row.patientId,
            professionalUserId: row.professionalUserId || null,
            professionalName,
            statuses: [],
            testLabels: [],
          });
        }

        const session = sessionMap.get(row.sessionId);
        if (!session) return;

        session.statuses.push(row.status || 'completed');

        const label = getAssessmentV2ResultLabel({
          testKey: String(row.testKey || 'Avaliação'),
          summaryJson: row.summaryJson,
        });
        if (!session.testLabels.includes(label)) {
          session.testLabels.push(label);
        }
      });

      const v2AssessmentEntries: ReportEntry[] = Array.from(sessionMap.values()).map((session) => {
        const testCount = session.testLabels.length || 1;

        return {
          id: session.sessionId,
          sourceType: 'assessment',
          title: buildAssessmentTitle(session.testLabels),
          startDate: session.assessmentDate,
          endDate: null,
          professionalKey: getProfessionalKey({
            professionalName: session.professionalName,
            userId: session.professionalUserId,
          }),
          professionalName: getProfessionalLabel({
            professionalName: session.professionalName,
            userId: session.professionalUserId,
          }),
          patientId: session.patientId,
          location: null,
          durationMinutes: null,
          statusLabel: getAssessmentSessionStatusLabel(session.statuses, testCount),
        };
      });

      if (!isCurrentRequest()) return;

      setAppointmentEntries(nextAppointmentEntries);
      setEvolutionEntries(nextEvolutionEntries);
      setAssessmentEntries([...legacyAssessmentEntries, ...v2AssessmentEntries]);
    } catch (error: any) {
      if (!isCurrentRequest()) return;

      console.error('Erro ao carregar relatórios da agenda:', error);
      toast.error(`Erro ao carregar relatórios da agenda: ${error?.message || 'falha desconhecida'}`);
    } finally {
      if (isCurrentRequest()) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const requestedClientId = clientId;
    const requestId = reportSourceRequestRef.current.requestId + 1;

    reportSourceRequestRef.current = {
      requestId,
      clientId: requestedClientId,
    };

    setAppointmentEntries([]);
    setEvolutionEntries([]);
    setAssessmentEntries([]);

    if (requestedClientId) {
      setIsLoading(true);
      void loadReportSources(requestedClientId, requestId);
    } else {
      setIsLoading(false);
    }

    return () => {
      const activeRequest = reportSourceRequestRef.current;
      if (activeRequest.requestId === requestId && activeRequest.clientId === requestedClientId) {
        reportSourceRequestRef.current = {
          requestId: activeRequest.requestId + 1,
          clientId: undefined,
        };
      }
    };
  }, [clientId, loadReportSources]);

  const combinedEntries = useMemo<ReportEntry[]>(
    () =>
      [...appointmentEntries, ...evolutionEntries, ...assessmentEntries].sort(
        (left, right) => moment(right.startDate).valueOf() - moment(left.startDate).valueOf(),
      ),
    [appointmentEntries, evolutionEntries, assessmentEntries],
  );

  const professionalOptions = useMemo(
    () =>
      Array.from(
        new Set(
          combinedEntries
            .map((entry) => entry.professionalName)
            .filter((value) => value && value !== 'Não atribuído'),
        ),
      ).sort((left, right) => left.localeCompare(right, 'pt-BR')),
    [combinedEntries],
  );

  const reportData = useMemo(() => {
    const entriesForProfessional =
      professionalFilter === 'all'
        ? combinedEntries
        : combinedEntries.filter((entry) => entry.professionalName === professionalFilter);

    let rangeStart: Date;
    let rangeEnd: Date;

    if (periodMode === 'all') {
      if (entriesForProfessional.length > 0) {
        const times = entriesForProfessional.map((entry) => parseISO(entry.startDate).getTime());
        rangeStart = new Date(Math.min(...times));
        rangeEnd = new Date(Math.max(...times));
      } else {
        rangeStart = startOfMonth(new Date());
        rangeEnd = endOfMonth(new Date());
      }
    } else {
      const normalizedRange = resolveMonthRange(startMonth, endMonth);
      rangeStart = normalizedRange.start;
      rangeEnd = normalizedRange.end;
    }

    const filteredEntries = entriesForProfessional.filter((entry) => {
      const entryDate = parseISO(entry.startDate);
      return entryDate >= rangeStart && entryDate <= rangeEnd;
    });

    const totals: ReportTotals = {
      appointments: filteredEntries.filter((entry) => entry.sourceType === 'appointment').length,
      evolutions: filteredEntries.filter((entry) => entry.sourceType === 'evolution').length,
      assessments: filteredEntries.filter((entry) => entry.sourceType === 'assessment').length,
      professionals: new Set(
        filteredEntries
          .filter((entry) => entry.professionalName !== 'Não atribuído')
          .map((entry) => entry.professionalKey),
      ).size,
    };

    const professionalSummaryMap = new Map<string, ProfessionalSummaryRow>();
    filteredEntries.forEach((entry) => {
      if (!professionalSummaryMap.has(entry.professionalKey)) {
        professionalSummaryMap.set(entry.professionalKey, {
          professionalKey: entry.professionalKey,
          professionalName: entry.professionalName,
          appointments: 0,
          evolutions: 0,
          assessments: 0,
          total: 0,
        });
      }

      const current = professionalSummaryMap.get(entry.professionalKey);
      if (!current) return;

      current.total += 1;

      if (entry.sourceType === 'appointment') current.appointments += 1;
      if (entry.sourceType === 'evolution') current.evolutions += 1;
      if (entry.sourceType === 'assessment') current.assessments += 1;
    });

    if (!filteredEntries.length) {
      return {
        filteredEntries,
        totals,
        chartRows: [] as Array<Record<string, string | number>>,
        trendKey: `${primaryMetric}_trend`,
        professionalRows: Array.from(professionalSummaryMap.values()),
        rangeStart,
        rangeEnd,
      };
    }

    const bucketDates = buildBucketDates(rangeStart, rangeEnd, granularity);
    const groupedEntries = new Map<string, ReportEntry[]>();

    filteredEntries.forEach((entry) => {
      const key = getBucketKey(parseISO(entry.startDate), granularity);
      if (!groupedEntries.has(key)) groupedEntries.set(key, []);
      groupedEntries.get(key)?.push(entry);
    });

    const chartRows = bucketDates.map((date) => {
      const key = getBucketKey(date, granularity);
      const entries = groupedEntries.get(key) || [];
      const appointments = entries.filter((entry) => entry.sourceType === 'appointment').length;
      const evolutions = entries.filter((entry) => entry.sourceType === 'evolution').length;
      const assessments = entries.filter((entry) => entry.sourceType === 'assessment').length;
      const professionals = new Set(
        entries
          .filter((entry) => entry.professionalName !== 'Não atribuído')
          .map((entry) => entry.professionalKey),
      ).size;

      return {
        label: getBucketLabel(date, granularity),
        appointments,
        evolutions,
        assessments,
        professionals,
      };
    });

    const trendKey = `${primaryMetric}_trend`;
    const movingAverage = computeMovingAverage(
      chartRows.map((row) => Number(row[primaryMetric] || 0)),
      3,
    );

    chartRows.forEach((row, index) => {
      row[trendKey] = Number(movingAverage[index].toFixed(2));
    });

    return {
      filteredEntries,
      totals,
      chartRows,
      trendKey,
      professionalRows: Array.from(professionalSummaryMap.values()).sort((left, right) => right.total - left.total),
      rangeStart,
      rangeEnd,
    };
  }, [combinedEntries, periodMode, startMonth, endMonth, professionalFilter, granularity, primaryMetric]);

  const activeMetricLabels = useMemo(() => {
    const labels: Record<string, string> = {};

    METRIC_DEFS.forEach((metric) => {
      labels[metric.key] = metric.label;
    });

    labels[reportData.trendKey] = `Tendência (${labels[primaryMetric] || 'principal'})`;
    return labels;
  }, [primaryMetric, reportData.trendKey]);

  const periodLabel = useMemo(() => {
    if (periodMode === 'all') {
      return `${format(reportData.rangeStart, 'MMM/yy', { locale: ptBR })} - ${format(reportData.rangeEnd, 'MMM/yy', {
        locale: ptBR,
      })}`;
    }

    const normalized = resolveMonthRange(startMonth, endMonth);
    return `${format(normalized.start, 'MMM/yy', { locale: ptBR })} - ${format(normalized.end, 'MMM/yy', {
      locale: ptBR,
    })}`;
  }, [periodMode, reportData.rangeEnd, reportData.rangeStart, startMonth, endMonth]);

  const lineMetricKeys = lineMetrics.filter((metric) => metric !== primaryMetric);

  const handleToggleLineMetric = (metricKey: MetricKey) => {
    if (metricKey === primaryMetric) return;

    setLineMetrics((previous) =>
      previous.includes(metricKey)
        ? previous.filter((value) => value !== metricKey)
        : [...previous, metricKey],
    );
  };

  const handlePrimaryMetricChange = (metricKey: MetricKey) => {
    setPrimaryMetric(metricKey);
    setLineMetrics((previous) => previous.filter((value) => value !== metricKey));
  };

  const handleExportCsv = () => {
    if (!reportData.filteredEntries.length) {
      toast.info('Sem dados para exportar.');
      return;
    }

    const rows = reportData.filteredEntries.map((entry) => ({
      'Data/Hora': moment(entry.startDate).format('DD/MM/YYYY HH:mm'),
      Origem: getSourceLabel(entry.sourceType),
      Titulo: entry.title,
      Profissional: entry.professionalName,
      Status: entry.statusLabel,
      Local: entry.location || '-',
      Duracao: entry.durationMinutes != null ? `${entry.durationMinutes} min` : '-',
    }));

    const csv = [
      Object.keys(rows[0]).join(','),
      ...rows.map((row) => Object.values(row).map((value) => `"${String(value).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `relatorio-agenda-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleExportPdf = async () => {
    if (!reportRef.current) return;

    try {
      setExportingPdf(true);
      const html2canvas = (await import('html2canvas')).default;

      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
      });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const imgData = canvas.toDataURL('image/png');

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`relatorio-agenda-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
      toast.success('PDF exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      toast.error('Erro ao exportar PDF.');
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-gray-800">Relatórios</h2>
          <p className="text-sm text-gray-500">
            Tendências e comparativos de agendamentos, evoluções e avaliações por clínica.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <ActionButton
            onClick={handleExportCsv}
            variant="success"
          >
            Exportar CSV
          </ActionButton>
          <ActionButton
            onClick={handleExportPdf}
            disabled={exportingPdf}
            variant="primary"
          >
            {exportingPdf ? 'Gerando PDF...' : 'Exportar PDF'}
          </ActionButton>
        </div>
      </div>

      <div className="rounded-lg bg-white p-6 shadow-sm">
        <div className="grid gap-4 md:grid-cols-4">
          <div className="space-y-1">
            <div className="text-xs uppercase text-gray-500">Período</div>
            <select
              value={periodMode}
              onChange={(event) => setPeriodMode(event.target.value as PeriodMode)}
              className="w-full rounded-md border-gray-300 text-sm"
            >
              <option value="range">Intervalo</option>
              <option value="all">Todos os meses</option>
            </select>
          </div>

          <div className="space-y-1">
            <div className="text-xs uppercase text-gray-500">Mês inicial</div>
            <input
              type="month"
              value={startMonth}
              onChange={(event) => setStartMonth(event.target.value)}
              disabled={periodMode === 'all'}
              className="w-full rounded-md border-gray-300 text-sm disabled:bg-gray-100"
            />
          </div>

          <div className="space-y-1">
            <div className="text-xs uppercase text-gray-500">Mês final</div>
            <input
              type="month"
              value={endMonth}
              onChange={(event) => setEndMonth(event.target.value)}
              disabled={periodMode === 'all'}
              className="w-full rounded-md border-gray-300 text-sm disabled:bg-gray-100"
            />
          </div>

          <div className="space-y-1">
            <div className="text-xs uppercase text-gray-500">Granularidade</div>
            <select
              value={granularity}
              onChange={(event) => setGranularity(event.target.value as Granularity)}
              className="w-full rounded-md border-gray-300 text-sm"
            >
              <option value="month">Mensal</option>
              <option value="week">Semanal</option>
              <option value="day">Diária</option>
            </select>
          </div>

          <div className="space-y-1">
            <div className="text-xs uppercase text-gray-500">Profissional</div>
            <select
              value={professionalFilter}
              onChange={(event) => setProfessionalFilter(event.target.value)}
              className="w-full rounded-md border-gray-300 text-sm"
            >
              <option value="all">Todos</option>
              {professionalOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <div className="text-xs uppercase text-gray-500">Métrica principal</div>
            <select
              value={primaryMetric}
              onChange={(event) => handlePrimaryMetricChange(event.target.value as MetricKey)}
              className="w-full rounded-md border-gray-300 text-sm"
            >
              {METRIC_DEFS.map((metric) => (
                <option key={metric.key} value={metric.key}>
                  {metric.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1 md:col-span-2">
            <div className="text-xs uppercase text-gray-500">Variáveis em linha</div>
            <div className="flex flex-wrap gap-2">
              {METRIC_DEFS.map((metric) => {
                const disabled = metric.key === primaryMetric;
                return (
                  <label
                    key={metric.key}
                    className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${
                      disabled ? 'border-gray-200 bg-gray-100 text-gray-400' : 'border-gray-200 bg-gray-50 text-gray-700'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={!disabled && lineMetrics.includes(metric.key)}
                      onChange={() => handleToggleLineMetric(metric.key)}
                      disabled={disabled}
                    />
                    {metric.label}
                  </label>
                );
              })}
            </div>
          </div>

          <div className="space-y-1">
            <div className="text-xs uppercase text-gray-500">Tendência</div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={showTrend}
                onChange={(event) => setShowTrend(event.target.checked)}
              />
              Mostrar linha de tendência
            </label>
          </div>
        </div>
      </div>

      <div ref={reportRef} className="space-y-6 rounded-lg bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Relatório consolidado da agenda</h3>
            <p className="mt-1 text-xs text-gray-500">
              Período: {periodLabel} • Granularidade:{' '}
              {granularity === 'month' ? 'Mensal' : granularity === 'week' ? 'Semanal' : 'Diária'} • Profissional:{' '}
              {professionalFilter === 'all' ? 'Todos' : professionalFilter}
            </p>
            <p className="text-xs text-gray-500">
              Inclui agendamentos, evoluções e avaliações consolidadas no período selecionado.
            </p>
          </div>

          <div className="text-xs text-gray-400">
            Gerado em {format(new Date(), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600"></div>
          </div>
        ) : reportData.filteredEntries.length ? (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
                <div className="text-xs uppercase text-blue-700">Agendamentos</div>
                <div className="text-3xl font-semibold text-blue-900">{reportData.totals.appointments}</div>
              </div>

              <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-4">
                <div className="text-xs uppercase text-emerald-700">Evoluções</div>
                <div className="text-3xl font-semibold text-emerald-900">{reportData.totals.evolutions}</div>
              </div>

              <div className="rounded-lg border border-amber-100 bg-amber-50 p-4">
                <div className="text-xs uppercase text-amber-700">Avaliações</div>
                <div className="text-3xl font-semibold text-amber-900">{reportData.totals.assessments}</div>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-md font-medium text-gray-900">Tendência por período</h4>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={reportData.chartRows} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip formatter={(value: any, name: string) => [value, activeMetricLabels[name] || name]} />
                    <Legend formatter={(value) => activeMetricLabels[String(value)] || String(value)} />
                    <Bar
                      dataKey={primaryMetric}
                      fill={METRIC_DEFS.find((metric) => metric.key === primaryMetric)?.color || '#2563eb'}
                      name={primaryMetric}
                    />
                    {showTrend ? (
                      <Line
                        type="monotone"
                        dataKey={reportData.trendKey}
                        stroke="#f97316"
                        strokeWidth={2}
                        dot={false}
                        name={reportData.trendKey}
                      />
                    ) : null}
                    {lineMetricKeys.map((metricKey) => (
                      <Line
                        key={metricKey}
                        type="monotone"
                        dataKey={metricKey}
                        stroke={METRIC_DEFS.find((metric) => metric.key === metricKey)?.color || '#64748b'}
                        strokeWidth={2}
                        dot={false}
                        name={metricKey}
                      />
                    ))}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-md font-medium text-gray-900">Resumo por período</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full border text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Período</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Agendamentos</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Evoluções</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Avaliações</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Profissionais únicos</th>
                      {showTrend ? (
                        <th className="px-3 py-2 text-left font-medium text-gray-600">
                          {activeMetricLabels[reportData.trendKey]}
                        </th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.chartRows.map((row) => (
                      <tr key={String(row.label)} className="border-t">
                        <td className="px-3 py-2 text-gray-900">{row.label}</td>
                        <td className="px-3 py-2 text-gray-900">{row.appointments}</td>
                        <td className="px-3 py-2 text-gray-900">{row.evolutions}</td>
                        <td className="px-3 py-2 text-gray-900">{row.assessments}</td>
                        <td className="px-3 py-2 text-gray-900">{row.professionals}</td>
                        {showTrend ? (
                          <td className="px-3 py-2 text-gray-900">{row[reportData.trendKey]}</td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-md font-medium text-gray-900">Resumo por profissional</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full border text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Profissional</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Agendamentos</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Evoluções</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Avaliações</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.professionalRows.map((row) => (
                      <tr key={row.professionalKey} className="border-t">
                        <td className="px-3 py-2 text-gray-900">{row.professionalName}</td>
                        <td className="px-3 py-2 text-gray-900">{row.appointments}</td>
                        <td className="px-3 py-2 text-gray-900">{row.evolutions}</td>
                        <td className="px-3 py-2 text-gray-900">{row.assessments}</td>
                        <td className="px-3 py-2 font-medium text-gray-900">{row.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-md font-medium text-gray-900">Detalhamento dos registros</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Data/Hora
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Título
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Origem
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Profissional
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Local
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Duração
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {reportData.filteredEntries.map((entry) => (
                      <tr key={`${entry.sourceType}-${entry.id}`}>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                          {moment(entry.startDate).format('DD/MM/YYYY HH:mm')}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{entry.title}</td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                          {getSourceLabel(entry.sourceType)}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                          {entry.professionalName}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">{entry.statusLabel}</td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">{entry.location || '-'}</td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                          {entry.durationMinutes != null ? `${entry.durationMinutes} min` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-gray-600">
            Nenhum dado encontrado para os filtros atuais.
          </div>
        )}
      </div>
    </div>
  );
};

export default AgendaReports;
